/* ============================================================
   Bundle Add to Cart — product-bundle.js

   Custom element: <bundle-add-to-cart>

   Adds the currently selected physical variant AND a linked
   digital variant (e.g. PDF) to the cart in a single
   /cart/add.js request, then opens Dawn's cart notification
   or drawer exactly as the standard add-to-cart button does.

   Metafield expected on the physical product:
     Namespace: custom
     Key:       linked_pdf_variant
     Type:      variant_reference
   ============================================================ */

if (!customElements.get('bundle-add-to-cart')) {
  customElements.define(
    'bundle-add-to-cart',
    class BundleAddToCart extends HTMLElement {

      connectedCallback() {
        this.btn              = this.querySelector('.bundle-add-to-cart__btn');
        this.priceEl          = this.querySelector('.bundle-add-to-cart__price');
        this.spinner          = this.querySelector('.loading__spinner');
        this.digitalVariantId = parseInt(this.dataset.digitalVariant, 10);
        this.digitalPrice     = parseInt(this.dataset.digitalPrice, 10);
        this.productFormId    = this.dataset.productFormId;
        this.sectionId        = this.dataset.sectionId;
        this.moneyFormat      = this.dataset.moneyFormat || '${{amount}}';
        this.bundleMode       = this.dataset.bundleMode || 'both';

        this.btn.addEventListener('click', this.handleClick.bind(this));

        // Keep disabled state in sync with the main add-to-cart button
        // (handles sold-out, unavailable, and loading states automatically)
        this._observeSubmitButton();

        // Update the displayed bundle total whenever the variant selector changes
        this._unsubVariantChange = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this._onVariantChange.bind(this)
        );
      }

      disconnectedCallback() {
        this._observer?.disconnect();
        this._unsubVariantChange?.();
      }

      // ── Private helpers ─────────────────────────────────────────

      // Strategy 1: look up the form by its HTML id attribute.
      // Strategy 2: traverse siblings in the same block container.
      // Returns the <form> element or null.
      _getProductForm() {
        if (this.productFormId) {
          const byId = document.getElementById(this.productFormId);
          if (byId) return byId;
        }
        // Fallback: the bundle element is a sibling of <product-form>.
        // Walk up one level and find the nearest add-to-cart form.
        return (
          this.parentElement?.querySelector('form[data-type="add-to-cart-form"]') || null
        );
      }

      _getSubmitButton() {
        const form = this._getProductForm();
        if (!form) return null;
        // The <form> is inside <product-form>; walk up then back down.
        const productFormEl = form.closest('product-form');
        return productFormEl?.querySelector('[type="submit"]') || null;
      }

      // Read the currently selected variant ID. Tries three sources in order:
      //  1. Hidden [name="id"] input inside the product form (live, updates on variant change)
      //  2. data-physical-variant attribute baked in at page load (static fallback)
      _getCurrentPhysicalVariantId() {
        const form = this._getProductForm();
        const fromForm = parseInt(form?.querySelector('[name="id"]')?.value || 0, 10);
        if (fromForm) return fromForm;

        // Static fallback — correct for single-variant products
        const fromAttr = parseInt(this.dataset.physicalVariant || 0, 10);
        if (!fromAttr) {
          console.warn('[Bundle] Could not resolve physical variant ID. form:', form, 'productFormId:', this.productFormId);
        }
        return fromAttr;
      }

      // Mirror only the HTML disabled attribute of the main submit button.
      // We intentionally ignore aria-disabled — Dawn sets that transiently
      // during its own add-to-cart loading, which would lock out the bundle
      // button while an unrelated add is in progress.
      _observeSubmitButton() {
        const submitBtn = this._getSubmitButton();
        if (!submitBtn) return;

        // Sync immediately
        this.btn.disabled = submitBtn.hasAttribute('disabled');

        this._observer = new MutationObserver(() => {
          this.btn.disabled = submitBtn.hasAttribute('disabled');
        });

        this._observer.observe(submitBtn, {
          attributes: true,
          attributeFilter: ['disabled'],
        });
      }

      // Called by Dawn's PubSub when the user changes a variant option
      _onVariantChange({ data }) {
        if (!data || data.sectionId !== this.sectionId) return;
        const variant = data.variant;
        if (!variant || !this.priceEl) return;

        // digital_only: price is always just the digital product — no recalc needed
        if (this.bundleMode === 'digital_only') return;

        // Recalculate and display the new bundle total
        const total = variant.price + this.digitalPrice;
        this.priceEl.textContent = _bundleFormatMoney(total, this.moneyFormat);
      }

      // ── Bundle notification helper ────────────────────────────────
      // After renderContents() places the physical item in the notification,
      // this method wraps that content in a .cart-item div, then appends
      // the digital item's card (also already rendered in the section HTML).

      _appendBundleNotificationItem(parsedState) {
        const digitalItem = parsedState.items[1];
        const sectionHtml = parsedState.sections['cart-notification-product'];
        if (!sectionHtml || !digitalItem?.key) return;

        const productContainer = document.getElementById('cart-notification-product');
        if (!productContainer) return;

        // The full section HTML contains every cart item — find the digital one
        const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const digitalCard = doc.querySelector(
          `[id="cart-notification-product-${digitalItem.key}"]`
        );
        if (!digitalCard) return;

        // Wrap the existing (physical) inner HTML in a matching .cart-item div
        const physicalCard = document.createElement('div');
        physicalCard.className = 'cart-item cart-notification-product__bundle-item';
        physicalCard.innerHTML = productContainer.innerHTML;

        // Rebuild the product container with both cards separated by a divider
        productContainer.classList.add('cart-notification-product--bundle');
        productContainer.innerHTML = '';
        productContainer.appendChild(physicalCard);

        const divider = document.createElement('div');
        divider.className = 'cart-notification-product__bundle-divider';
        productContainer.appendChild(divider);

        digitalCard.classList.add('cart-notification-product__bundle-item');
        productContainer.appendChild(digitalCard);
      }

      // ── Click handler ────────────────────────────────────────────

      async handleClick() {
        if (!this.digitalVariantId || isNaN(this.digitalVariantId)) {
          console.error('[Bundle] Invalid digital variant ID:', this.dataset.digitalVariant);
          return;
        }

        // digital_only mode: skip physical variant lookup entirely
        const physicalId = this.bundleMode === 'digital_only'
          ? null
          : this._getCurrentPhysicalVariantId();

        if (this.bundleMode !== 'digital_only' && !physicalId) return;

        const cartEl =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        // Enter loading state
        this.btn.classList.add('loading');
        this.btn.setAttribute('aria-disabled', 'true');
        this.spinner?.classList.remove('hidden');

        try {
          // Ask for the same sections Dawn would request, so renderContents
          // has the pre-rendered HTML it needs to update the cart UI.
          const sections =
            cartEl?.getSectionsToRender?.()?.map((s) => s.id) ?? [];

          // Hidden line item property (leading underscore) used by Shopify Flow
          // to add an order tag like "bundle" when the order is created.
          const bundleProps = {
            _bundle: this.bundleMode === 'digital_only' ? 'digital-only' : 'physical+digital',
          };

          const response = await fetch(
            window.Shopify.routes.root + 'cart/add.js',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify({
                items: this.bundleMode === 'digital_only'
                  ? [{ id: this.digitalVariantId, quantity: 1, properties: bundleProps }]
                  : [
                      { id: physicalId, quantity: 1, properties: bundleProps },
                      { id: this.digitalVariantId, quantity: 1, properties: bundleProps },
                    ],
                sections,
                sections_url: window.location.pathname,
              }),
            }
          );

          const parsedState = await response.json();

          if (!response.ok) {
            // Shopify returned an error (sold out, invalid variant, etc.)
            console.error('[Bundle] Cart error (HTTP ' + response.status + '):', parsedState);
            return;
          }

          if (!cartEl) {
            // No cart notification / drawer on this theme setup — fall back
            window.location = window.Shopify.routes.root + 'cart';
            return;
          }

          // cart-notification.renderContents() uses parsedState.key to find
          // the notification product card in the rendered section HTML.
          // When adding multiple items the response contains an items array,
          // so we expose the physical product's key at the top level.
          parsedState.key = parsedState.items?.[0]?.key;

          if (cartEl.classList.contains('is-empty'))
            cartEl.classList.remove('is-empty');

          // Guard: if Shopify didn't return section HTML (e.g. sections param
          // was empty or unrecognised), fall back to redirecting to the cart.
          if (!parsedState.sections) {
            window.location = window.Shopify.routes.root + 'cart';
            return;
          }

          cartEl.renderContents(parsedState);

          // Bundle: inject the digital item card into the notification
          if (this.bundleMode === 'both' && parsedState.items?.length > 1) {
            this._appendBundleNotificationItem(parsedState);
          }

        } catch (err) {
          console.error('[Bundle] Add to cart failed:', err);
        } finally {
          this.btn.classList.remove('loading');
          this.btn.removeAttribute('aria-disabled');
          this.spinner?.classList.add('hidden');
        }
      }
    }
  );
}

/* ── Money formatter ─────────────────────────────────────────
   Formats a cent value using the shop's Liquid money_format
   string (passed via data-money-format on the element).
   Handles all four Shopify format tokens.
   ─────────────────────────────────────────────────────────── */
function _bundleFormatMoney(cents, format) {
  if (!format) format = '${{amount}}';
  const amount    = (cents / 100).toFixed(2);
  const amountInt = String(Math.round(cents / 100));
  return format
    .replace('{{amount}}',                                  amount)
    .replace('{{amount_no_decimals}}',                      amountInt)
    .replace('{{amount_with_comma_separator}}',             amount.replace('.', ','))
    .replace('{{amount_no_decimals_with_comma_separator}}', amountInt.replace('.', ','));
}
